import { HANDLE_SIZE_PX } from '../../constants/whiteboard-config.constants.js';
import type { BoardObject } from '../../core/interfaces/board-object.interface.js';
import type { TextBox } from './textbox.interface.js';
import type { ObjectId } from '../../core/bus/branded-types.js';

/**
 * UI action dispatched when the user clicks the overflow indicator to auto-grow
 * a clipped textbox (PRD-RTE-OVERFLOW §TR6). `requiredHeight` is the pre-measured
 * world-unit target height (see `OverflowMeasurement`); the action is added to
 * the `UiAction` union in `ui-action.events.ts` (T4.1) and translated into an
 * `UpdatePropertyCommand(id, 'height', requiredHeight)` by the lifecycle
 * controller (T4.3). `id` is the branded `ObjectId`, matching the sibling
 * `textbox/*` variants.
 */
export interface FitToContentAction {
  readonly type: 'textbox/fit-to-content';
  readonly id: ObjectId;
  readonly requiredHeight: number;
}

/**
 * Gap between the overflow indicator and the corner handle, in CSS px.
 *
 * The corner resize handle is centred ON the corner, so it reaches
 * `HANDLE_SIZE_PX / 2` inwards from it. The indicator's inset must clear that
 * half-extent plus this gap, or the two overlap (Anke, 2026-07-18).
 */
const OVERFLOW_INDICATOR_GAP_PX = 4;

/**
 * Layout constants for the overflow indicator — the InDesign-style "+" overset
 * marker shown bottom-right when textbox content is clipped (PRD-RTE-OVERFLOW
 * §TR3).
 *
 * Anke, 2026-07-18: measurements are in CSS PIXELS, not world units, and the
 * renderer divides them by the viewport scale — so the marker stays the same
 * size on screen at every zoom level, exactly like the selection handles. It was
 * previously sized in world units, which made it grow and shrink with the zoom.
 * `SIZE_PX` is deliberately not a literal: the marker must stay the same size as
 * the corner handles, so it reads as part of the same family.
 */
export const OVERFLOW_INDICATOR = {
  /** Square edge length in CSS px — same square as the selection handles. */
  SIZE_PX: HANDLE_SIZE_PX,
  /** Inset from the textbox's bottom-right corner in CSS px (clears the handle). */
  PAD_PX: HANDLE_SIZE_PX / 2 + OVERFLOW_INDICATOR_GAP_PX,
  /** Square stroke colour. */
  STROKE: '#888',
  /** Square fill colour. */
  FILL: '#ffffff',
  /** Glyph rendered centered in the square. */
  GLYPH: '+',
  /**
   * Anke, 2026-07-18 — the "+" read too thin against the box outline, so it is
   * drawn bold. Konva takes the weight as a `fontStyle` string, not a number.
   */
  GLYPH_WEIGHT: 'bold',
  /**
   * Downward nudge of the glyph inside the square, in CSS px.
   *
   * Konva centres a `Text` on its LINE box, and a "+" does not fill that box
   * symmetrically — its ink sits above the line's vertical middle, so a
   * geometrically centred glyph reads about a pixel high (Anke, 2026-07-18).
   * Optical centring beats geometric centring here.
   */
  GLYPH_NUDGE_PX: 1,
  /**
   * Glyph colour WHILE HOVERED. The box darkens to the handle hover fill on
   * hover, which would swallow a grey "+", so the glyph inverts to the box's
   * resting colour and stays legible.
   */
  GLYPH_HOVER_FILL: '#ffffff',
} as const;

/**
 * Type-narrowing guard for textbox board objects (PRD-F6 §TR14).
 *
 * Used by the placement controller, editor controller and renderer registry
 * fallback to fail silently for non-textbox payloads (FC10).
 */
export function isTextBox(obj: BoardObject): obj is TextBox {
  return obj.type === 'text';
}

/**
 * Re-rasterization threshold (PRD-RTE §TR4).
 *
 * The renderer rasterizes a new bitmap only when the absolute relative scale
 * delta (|newScale - lastScale| / lastScale) exceeds 5%. Smaller zoom changes
 * keep the existing bitmap to avoid expensive SVG → image round-trips.
 */
export const RE_RASTERIZE_THRESHOLD = 0.05;

/**
 * Konva custom-attribute key holding the last scale at which the bitmap was
 * rasterized. Compared in `updateNode` against the current viewport scale to
 * decide whether re-rasterization is required (PRD-RTE §TR4).
 */
export const LAST_COMPUTED_SCALE_ATTR = '_lastComputedScale';

/**
 * Konva custom-attribute key holding the last render-hash (covers text + all
 * style fields). Replaces the legacy `LAST_TEXT_ATTR` so style-only changes
 * (color, fontWeight, listType, …) trigger immediate re-rasterization without
 * waiting for a zoom event (PRD-RTE §TR4 / acceptance criterion "style change").
 */
export const LAST_RENDER_HASH_ATTR = '_lastRenderHash';

/**
 * 1×1 fully transparent PNG used as the initial `Konva.Image.image` value
 * before the first asynchronous SVG raster has loaded (PRD-RTE §TR3).
 *
 * Lazy module-top-level construction so the file stays importable in
 * non-DOM environments (SSR / vitest pre-setup) — the guard returns a
 * minimal stub when `Image` is not defined.
 */
function createEmptyBitmap(): HTMLImageElement {
  if (typeof Image === 'undefined') {
    return {} as HTMLImageElement;
  }
  const img = new Image(1, 1);
  img.src =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
  return img;
}

export const EMPTY_BITMAP: HTMLImageElement = createEmptyBitmap();

/**
 * Rasterization request pushed into the renderer's `Subject<RasterRequest>`
 * (PRD-RTE §TR2). `switchMap` discards in-flight loads when a newer request
 * arrives so a slow first raster never overwrites a fresh second raster.
 */
export interface RasterRequest {
  readonly obj: Readonly<TextBox>;
  readonly scale: number;
  readonly dpr: number;
}
